import { Injectable } from '@angular/core';
import { DbContext, DbTable } from '../data/dbcontext.service';
import { HttpClient } from '@angular/common/http';
import { DateService } from '../date/date.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { Observable } from 'rxjs/Observable';
import { VesselCrewEditor, VesselCrewOvertimeEditor } from '../../models/vessel-crew/vessel-crew.models';
import { EnumSummary } from '../../models/base/base.model';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { AppConfig } from '../../app/app.config';
import { cloneDeep } from 'lodash';

@Injectable()
export class VesselCrewService {
  config = AppConfig.settings;
  private readonly vesselCrewTable: DbTable;
  private readonly overtimeRequestedBysTable: DbTable;

  constructor(
    private http: HttpClient,
    private dbContext: DbContext,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private timeZoneService: TimeZoneService
  ) {
    this.vesselCrewTable = this.dbContext.tables.find(t => t.name === 'VesselCrewData');
    this.overtimeRequestedBysTable = this.dbContext.tables.find(t => t.name === 'OvertimeRequestedBysData');
  }

  // note that resulting editor.date has not been deserialised here, as all we need is the id
  async saveEditor(editor: VesselCrewEditor): Promise<VesselCrewEditor> {
    const editorToSave = cloneDeep(editor);
    editorToSave.date.dateTime = this.dateParserService.removeOffsetFromLuxonDate(editorToSave.date.dateTime);
    const url = `${this.config.apiUrl}api/vesselcrew/save`;
    return this.http.post<VesselCrewEditor>(url, editorToSave).toPromise();
  }

  deleteAllVesselCrewRecords() {
    return this.dbContext.removeAll(this.vesselCrewTable);
  }

  async saveVesselCrewChangeMultipleRecords(records: VesselCrewEditor[]) {
    records = this.dateParserService.removeOffsetFromLuxonDates(records);
    for (let i = 0; i < records.length; i++) {
      const recordToStore = cloneDeep(records[i]);
      await this.dbContext.addOrUpdate(this.vesselCrewTable, recordToStore.id, recordToStore);
    }
  }

  async getVesselCrewDataForDay(date?: PtLuxonDate): Promise<VesselCrewEditor> {
    let instances = await this.dbContext.getAll(this.vesselCrewTable) as VesselCrewEditor[];

    instances = await this.applyOffsetsToVesselCrewTimes(instances);

    const filteredInstances: VesselCrewEditor[] = [];

    for (let i = 0; i < instances.length; i++) {
      const sameDay = await this.dateService.compareDayOffsetsPreApplied(date.dateTime, instances[i].date.dateTime);
      if (sameDay) {
        filteredInstances.push(instances[i]);
      }
    }

    let crewEditor = filteredInstances[0];

    if (!!crewEditor) {
      return cloneDeep(crewEditor);
    }

    crewEditor = new VesselCrewEditor();
    crewEditor.date = date;

    return crewEditor;
  }

  validate(editor: VesselCrewEditor): Observable<void> {
    const editorToValidate = cloneDeep(editor);
    editorToValidate.date.dateTime = this.dateParserService.removeOffsetFromLuxonDate(editorToValidate.date.dateTime);
    const url = `${this.config.apiUrl}api/vesselcrew/validate`;
    return this.http.post<void>(url, editorToValidate);
  }

  validateOvertime(editor: VesselCrewOvertimeEditor): Observable<void> {
    const url = `${this.config.apiUrl}api/vesselcrew/overtime/validate`;
    return this.http.post<void>(url, editor);
  }

  deleteOvertimeRequestedBys() {
    return this.dbContext.removeAll(this.overtimeRequestedBysTable);
  }

  async saveOvertimeRequestedBys(overtimeRequestedBys: EnumSummary[]) {
    for (let i = 0; i < overtimeRequestedBys.length; i++) {
      await this.dbContext.addOrUpdate(this.overtimeRequestedBysTable, overtimeRequestedBys[i].id, overtimeRequestedBys[i]);
    }
  }

  async getOvertimeRequestedBys(): Promise<EnumSummary[]> {
    return await this.dbContext.getAll(this.overtimeRequestedBysTable) as EnumSummary[];
  }

  async applyOffsetsToVesselCrewTimes(
    instances: VesselCrewEditor[],
  ): Promise<VesselCrewEditor[]> {
    for (let i = 0; i < instances.length; i++) {
      if (instances[i].date) {
        instances[i].date.dateTime = await this.dateParserService.deserializeDate(instances[i].date.dateTime, true, this.timeZoneService);
      }
    }

    return instances;
  }
}



// WEBPACK FOOTER //
// ./src/services/vessel-crew/vessel-crew.service.ts